'use client';

import React, { useEffect } from 'react';
import Image from 'next/image';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Box, Container, Group, Stack, Text, Title } from '@mantine/core';
import { useResponsive } from '@/hooks/use-responsive';
import Button from '../Button';
import { HeroContent } from '@/utils/types/hero';
import { MarkdownContent } from '@/app/page';

interface StyleProps {
  $responsive: (styles: { xs?: any; sm?: any; md?: any; lg?: any }) => any;
  $content: HeroContent;
}

interface HeroProps {
  markdownContent?: MarkdownContent;
}

const Root = styled('div')<StyleProps>`
  ${({ $responsive, $content }) => css`
    position: relative;
    display: flex;
    flex-direction: column;
    min-height: 95vh;
    background: ${$content.theme.colors.background};

    > div {
      position: relative;
      z-index: 1;
      flex: 1;
      display: flex;
      flex-direction: column;
    }

    .background-media {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      opacity: ${$content.theme.video.opacity};
    }

    .hero-text {
      max-width: ${$responsive({
        xs: $content.mobile.layout.width,
        md: $content.tablet.layout.width,
        lg: $content.desktop.layout.width,
      })};
      text-align: ${$responsive({
        xs: $content.mobile.layout.alignment,
        md: $content.tablet.layout.alignment,
      })};
      color: ${$content.theme.colors.text};

      h1 {
        font-size: ${$responsive({
          xs: $content.mobile.typography.titleSize,
          md: $content.tablet.typography.titleSize,
          lg: $content.desktop.typography.titleSize,
        })};
        line-height: 1.2;
        margin-bottom: 1rem;
      }

      p {
        font-size: ${$responsive({
          xs: $content.mobile.typography.textSize,
          md: $content.tablet.typography.textSize,
          lg: $content.desktop.typography.textSize,
        })};
        font-weight: 300;
        line-height: 1.5;
      }
    }
  `}
`;

const BackgroundMedia = ({ src }: { src: string }) => {
  const isVideo = src.endsWith('.mp4') || src.endsWith('.webm');

  return isVideo ? (
    <video 
      autoPlay 
      muted 
      loop 
      className="background-media"
      playsInline
    >
      <source src={src} type={`video/${src.split('.').pop()}`} />
    </video>
  ) : (
    <Image
      src={src}
      alt="background"
      fill
      className="background-media"
      priority
      quality={100}
    />
  );
};
export default function Hero({ markdownContent }: HeroProps) {
  const content = markdownContent?.frontmatter;
  const { responsive } = useResponsive();

  useEffect(() => {
    // console.log('Hero Section Markdown Content:', markdownContent);
  }, [markdownContent]);

  if (!content) return null;
  return (
    <Root $responsive={responsive} $content={content as HeroContent}>
      <Container size={content.container.maxWidth}>
        <Stack justify="space-between" flex={1}>
          <Box h={content.container.verticalPadding.top} />
          <Box className="hero-text">
            <Title order={1}>{content.title}</Title>
            <Text>{content.description}</Text>
          </Box>

          <Group
            pb={content.container.verticalPadding.bottom}
            gap={content.container.spacing.buttonGap}
            justify={responsive({
              xs: content.mobile.layout.alignment,
              md: content.tablet.layout.alignment,
            })}
          >
            <Button 
              href={content.buttonPrimary.url} 
              size="large" 
              variant="filled" 
              color="white"
            >
              {content.buttonPrimary.label}
            </Button>

            <Text
              c={content.theme.colors.text}
              size="sm"
              maw={250}
              ta={responsive({
                xs: content.mobile.layout.alignment,
                md: content.tablet.layout.alignment,
              })}
            >
              {content.pricingText}
            </Text>
          </Group>
        </Stack>
      </Container>
      <BackgroundMedia src={content.backgroundVideo} />
    </Root>
  );
}